import {
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  FilterOutlined,
  MailOutlined,
  MoreOutlined,
  PhoneOutlined,
  PlusOutlined,
  UserOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import {
  App,
  Avatar,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { ApiUser, Province } from '../../types';

const { Search } = Input;
const { Option } = Select;
const { Title } = Typography;

const ReassignLeadModal = ({ visible, onCancel, lead, onReassignSubmit, message }: any) => {
  const [form] = Form.useForm();
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [loading, setLoading] = useState(false);

  const title = `Reassign Lead: ${lead?.full_name || ''}`;

  useEffect(() => {
    if (visible && lead) {
      form.resetFields();
      setLoading(true);
      api
        .get(`leads/users`, { params: { property_area: lead.property_area?.id } })
        .then((response) => {
          const allUsers: ApiUser[] = response.data.data || [];
          // Filter out the currently assigned user
          const filteredUsers = allUsers.filter((user: ApiUser) => user.id !== lead.assigned_to?.id);
          setUsers(filteredUsers);
        })
        .catch((error) => {
          console.error('Failed to fetch users:', error);
          message.error('Gagal memuat data pengguna untuk area properti ini.');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [lead, visible, form, message]);

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        if (values.assigned_to_id === lead.assigned_to?.id) {
          message.info('Tidak ada perubahan pada penugasan.');
          onCancel();
          return;
        }
        onReassignSubmit(lead.id, values.assigned_to_id, values.reason);
        form.resetFields();
      })
      .catch((info) => {
        console.log('Validate Failed:', info);
      });
  };

  return (
    <Modal
      title={title}
      visible={visible}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={loading}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <p>
          <strong>Saat ini ditugaskan ke:</strong> {lead?.assigned_to?.full_name || 'Tidak ada'}
        </p>
        <Form.Item
          name="assigned_to_id"
          label="Reassign kepada"
          rules={[{ required: true, message: 'Silakan pilih pengguna!' }]}
        >
          <Select
            showSearch
            placeholder="Pilih sales agent"
            loading={loading}
            optionFilterProp="children"
            filterOption={(input, option: any) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {users.map((user) => (
              <Option key={user.id} value={user.id}>
                {user.full_name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="reason"
          label="Alasan Reassign"
          rules={[{ required: true, message: 'Silakan masukkan alasan!' }]}
        >
          <Input.TextArea rows={4} placeholder={`Jelaskan alasan pemindahan sales agent`} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const LeadsList = () => {
  const [leads, setLeads] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Master Data Options for Filters
  const [leadStatuses, setLeadStatuses] = useState<any[]>([]);
  const [leadFollowUpStatuses, setLeadFollowUpStatuses] = useState<any[]>([]);
  const [propertyAreas, setPropertyAreas] = useState<any[]>([]);
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [leadSources, setLeadSources] = useState<any[]>([]);

  const [reassignModalVisible, setReassignModalVisible] = useState(false);
  const [selectedLead, setSelectedLead] = useState<any>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const { message } = App.useApp();

  const shouldShowPropertyAreaFilter = hasPermission('lead.property');

  // Local state untuk controlled input text pencarian (diinisialisasi dari searchParams URL)
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const isTypingRef = useRef(false);

  // Helper untuk memperbarui URL Query Search Params
  const updateFilterParam = useCallback(
    (newParams: Record<string, string | number | undefined | null>, resetPage = true) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(newParams).forEach(([key, val]) => {
            if (val !== undefined && val !== null && String(val).trim() !== '') {
              next.set(key, String(val).trim());
            } else {
              next.delete(key);
            }
          });
          if (resetPage) {
            next.delete('page');
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Sinkronisasi searchTerm dengan parameter 'search' di URL (Debounced 400ms saat mengetik)
  useEffect(() => {
    if (!isTypingRef.current) return;

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      isTypingRef.current = false;
      const currentUrlSearch = searchParams.get('search') || '';
      if (searchTerm.trim() !== currentUrlSearch.trim()) {
        updateFilterParam({ search: searchTerm.trim() || undefined }, true);
      }
    }, 400);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [searchTerm, searchParams, updateFilterParam]);

  // Sinkronisasi balik dari URL hanya jika pengguna sedang TIDAK mengetik (misal: navigasi Back/Forward, Reset)
  useEffect(() => {
    if (!isTypingRef.current) {
      const urlSearch = searchParams.get('search') || '';
      if (urlSearch !== searchTerm) {
        setSearchTerm(urlSearch);
      }
    }
  }, [searchParams, searchTerm]);

  // Migrasi ramah backwards-compatible jika ada komponen lama yang mengirimkan via location.state
  useEffect(() => {
    if (location.state && Object.keys(location.state).length > 0) {
      const stateUpdate: Record<string, string> = {};
      if (location.state.statusId) stateUpdate.tahapanFU = location.state.statusId;
      if (location.state.leadSourceId) stateUpdate.source = location.state.leadSourceId;
      if (location.state.provinsiId) stateUpdate.provinsi_id = location.state.provinsiId;
      if (location.state.property_area_id) stateUpdate.property_area_id = location.state.property_area_id;
      if (location.state.StatusFU) stateUpdate.StatusFU = location.state.StatusFU;

      if (Object.keys(stateUpdate).length > 0) {
        updateFilterParam(stateUpdate, true);
        navigate(location.pathname + location.search, { replace: true, state: {} });
      }
    }
  }, [location.state, location.pathname, location.search, navigate, updateFilterParam]);

  // Fetch Master Data untuk dropdown filter
  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const requests: Promise<any>[] = [
          api.get('lead-status'),
          api.get('lead-follow-up-status'),
          api.get('provinsi'),
          api.get('lead-sources'),
        ];

        if (shouldShowPropertyAreaFilter) {
          requests.push(api.get('property-areas/role'));
        }

        const results = await Promise.allSettled(requests);
        const [statusesRes, followUpStatusesRes, provincesRes, sourcesRes, propertyAreasRes] = results;

        if (statusesRes.status === 'fulfilled') {
          setLeadStatuses((statusesRes.value.data?.data || []).filter((item: any) => item.is_active));
        }
        if (followUpStatusesRes.status === 'fulfilled') {
          setLeadFollowUpStatuses(
            (followUpStatusesRes.value.data?.data || []).filter((item: any) => item.is_active)
          );
        }
        if (provincesRes.status === 'fulfilled') {
          setProvinces(provincesRes.value.data?.data || []);
        }
        if (sourcesRes.status === 'fulfilled') {
          setLeadSources((sourcesRes.value.data?.data || []).filter((item: any) => item.is_active));
        }
        if (shouldShowPropertyAreaFilter && propertyAreasRes && propertyAreasRes.status === 'fulfilled') {
          setPropertyAreas((propertyAreasRes.value.data?.data || []).filter((item: any) => item.is_active));
        }
      } catch (error) {
        console.error('Failed to fetch filter data:', error);
        message.error('Gagal memuat data master untuk filter');
      }
    };

    fetchFilterData();
  }, [shouldShowPropertyAreaFilter, message]);

  // Mengambil daftar leads dengan parameter tersinkronisasi dari searchParams URL
  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const page = Number(searchParams.get('page')) || 1;
      const limit = Number(searchParams.get('limit')) || 10;

      const params: Record<string, any> = {
        page,
        limit,
      };

      const search = searchParams.get('search');
      if (search) params.search = search;

      const tahapanFU = searchParams.get('tahapanFU');
      if (tahapanFU) params.tahapanFU = tahapanFU;

      const StatusFU = searchParams.get('StatusFU');
      if (StatusFU) params.StatusFU = StatusFU;

      const property_area_id = searchParams.get('property_area_id');
      if (property_area_id) params.property_area_id = property_area_id;

      const source = searchParams.get('source');
      if (source) params.source = source;

      const provinsi_id = searchParams.get('provinsi_id');
      if (provinsi_id) params.provinsi_id = provinsi_id;

      const assigned_to = searchParams.get('assigned_to');
      if (assigned_to) params.assigned_to = assigned_to;

      const response = await api.get('leads', { params });
      const { data, meta } = response.data;

      setLeads(data || []);
      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize: limit,
        total: meta?.total || 0,
      }));
    } catch (error: any) {
      console.error('Failed to fetch leads:', error);
      message.error('Gagal memuat data leads');
    } finally {
      setLoading(false);
    }
  }, [searchParams, message]);

  // Trigger fetchLeads setiap kali searchParams di URL berubah
  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleTableChange = (newPagination: any) => {
    updateFilterParam(
      {
        page: newPagination.current,
        limit: newPagination.pageSize,
      },
      false
    );
  };

  const handleResetFilter = () => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    isTypingRef.current = false;
    setSearchTerm('');
    setSearchParams({}, { replace: true });
  };

  const handleDelete = async (id: any) => {
    try {
      await api.delete(`leads/${id}`);
      message.success('Lead berhasil dihapus');
      fetchLeads();
    } catch (error: any) {
      console.error('Failed to delete lead:', error);
      message.error('Gagal menghapus lead');
    }
  };

  const handleReassign = (lead: any) => {
    setSelectedLead(lead);
    setReassignModalVisible(true);
  };

  const handleAssignSubmit = async (leadId: string, assignedToId: string) => {
    try {
      await api.post(`leads/${leadId}/assign`, { assigned_to_id: assignedToId });
      message.success('Lead berhasil di-assign');
      setReassignModalVisible(false);
      fetchLeads();
    } catch (error: any) {
      console.error('Failed to assign lead:', error);
      message.error(error.response?.data?.message || 'Gagal meng-assign lead');
    }
  };

  const handleReassignSubmit = async (leadId: string, assignedToId: string, reason: string) => {
    try {
      await api.post(`leads/${leadId}/reassign`, { agent_id: assignedToId, reason: reason });
      message.success('Lead berhasil di-reassign');
      setReassignModalVisible(false);
      fetchLeads();
    } catch (error: any) {
      console.error('Failed to reassign lead:', error);
      message.error(error.response?.data?.message || 'Gagal me-reassign lead');
    }
  };

  // Cek apakah ada filter yang sedang aktif
  const hasActiveFilters = useMemo(() => {
    const keys = ['search', 'tahapanFU', 'StatusFU', 'property_area_id', 'source', 'provinsi_id', 'assigned_to'];
    return keys.some((k) => Boolean(searchParams.get(k)));
  }, [searchParams]);

  // Nama provinsi yang sedang aktif dipilih
  const activeProvinceName = useMemo(() => {
    const activeId = searchParams.get('provinsi_id');
    if (!activeId) return null;
    return provinces.find((p) => p.id_provinsi === activeId)?.nama_provinsi || activeId;
  }, [searchParams, provinces]);

  const actionMenuItems = (record: any): any[] => {
    const items: any[] = [];

    if (hasPermission('lead.view')) {
      items.push({
        key: 'view',
        icon: <EyeOutlined />,
        label: 'Detail',
        onClick: () => navigate(`/leads/${record.id}`),
      });
    }

    if (hasPermission('lead.update')) {
      items.push({
        key: 'edit',
        icon: <EditOutlined />,
        label: 'Edit',
        onClick: () => navigate(`/leads/${record.id}/edit`),
      });
    }

    if (hasPermission('lead.reassigned') && record.assigned_to) {
      items.push({
        key: 'reassign',
        icon: <UserSwitchOutlined />,
        label: 'Reassign',
        onClick: () => handleReassign(record),
      });
    }

    if (hasPermission('lead.delete')) {
      if (items.length > 0 && items[items.length - 1].type !== 'divider') {
        items.push({ type: 'divider' });
      }
      items.push({
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Hapus',
        danger: true,
        onClick: () => handleDelete(record.id),
      });
    }

    return items;
  };

  const columns: any[] = [
    {
      title: 'Lead Info',
      key: 'lead_info',
      width: 280,
      render: (_: any, record: any) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
            <Avatar size="small" icon={<UserOutlined />} style={{ marginRight: 8 }} />
            <strong>{record.full_name}</strong>
          </div>
          <div style={{ fontSize: 12, color: '#666' }}>
            {record.email && (
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                <MailOutlined style={{ marginRight: 4 }} />
                {record.email}
              </div>
            )}
            {record.phone && (
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                <PhoneOutlined style={{ marginRight: 4 }} />
                {record.phone}
              </div>
            )}
            {record.provinsi_name && (
              <div style={{ display: 'flex', alignItems: 'center', color: '#64748b', marginTop: 2 }}>
                <EnvironmentOutlined style={{ marginRight: 4, color: '#7BAA18' }} />
                <span>
                  {record.provinsi_name}
                  {record.kabupaten_kota_name ? `, ${record.kabupaten_kota_name}` : ''}
                </span>
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Sumber',
      dataIndex: ['lead_source', 'name'],
      key: 'lead_source',
      width: 160,
      render: (text: string) => <Tag color="blue">{text || '-'}</Tag>,
    },
    {
      title: 'Tipe Properti',
      dataIndex: ['property_type', 'name'],
      key: 'property_type',
      width: 140,
      render: (text: string) => <Tag>{text || '-'}</Tag>,
    },
    {
      title: 'Property Area',
      dataIndex: ['property_area', 'name'],
      key: 'property_area',
      width: 150,
      render: (text: string) => text || '-',
    },
    {
      title: 'Tahapan',
      dataIndex: ['lead_status', 'name'],
      key: 'tahapan',
      width: 120,
      render: (status: string) => <Tag>{status?.toUpperCase() || 'N/A'}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: ['lead_follow_up_status', 'name'],
      key: 'status',
      width: 120,
      render: (status: string) => {
        if (!status) return <Tag>-</Tag>;
        const statusName = status.toLowerCase();
        let color = 'default';
        if (statusName === 'hot') color = 'error';
        else if (statusName === 'rsv') color = 'green';
        else if (statusName === 'cold') color = 'processing';
        else if (statusName === 'o k') color = 'success';
        return <Tag color={color}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Status Ketertarikan',
      dataIndex: 'interest_status',
      key: 'interest_status',
      width: 170,
      render: (interestStatus: string) => {
        if (!interestStatus) return <Tag>-</Tag>;

        const formatToTitleCase = (str: string) => {
          if (!str) return '';
          return str
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        };

        const colorMap: { [key: string]: string } = {
          minat_pesan: 'green',
          minat_proses: 'processing',
          minat_tidak_pesan: 'warning',
          tidak_minat: 'error',
        };

        const color = colorMap[interestStatus] || 'default';
        const displayText = formatToTitleCase(interestStatus);

        return <Tag color={color}>{displayText}</Tag>;
      },
    },
    {
      title: 'Assigned To',
      dataIndex: ['assigned_to', 'full_name'],
      key: 'assigned_to',
      width: 150,
      render: (text: string) => text || '-',
    },
    {
      title: 'Aksi',
      key: 'action',
      fixed: 'right',
      width: 90,
      render: (_: any, record: any) => {
        const menuItems = actionMenuItems(record);

        // Only render the dropdown if there are any actions available for the user.
        return menuItems.length > 0 ? (
          <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        ) : null;
      },
    },
  ];

  return (
    <div>
      {/* Header Halaman */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            Manajemen Leads
          </Title>
        </Col>
        <Col>
          <Space>
            {hasPermission('lead.create') && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/leads/create')}>
                Tambah Lead
              </Button>
            )}
            <Button onClick={handleResetFilter} disabled={!hasActiveFilters}>
              Reset Filter
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Filter Card */}
      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          {/* Pencarian Teks */}
          <Col xs={24} sm={12} md={8} lg={6}>
            <Search
              placeholder="Cari nama, email, hp..."
              value={searchTerm}
              onChange={(e) => {
                isTypingRef.current = true;
                setSearchTerm(e.target.value);
              }}
              onSearch={(val) => {
                if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
                isTypingRef.current = false;
                updateFilterParam({ search: val?.trim() || undefined }, true);
              }}
              allowClear
              style={{ width: '100%' }}
            />
          </Col>

          {/* Filter Tahapan */}
          <Col xs={24} sm={12} md={8} lg={4}>
            <Select
              placeholder="Tahapan"
              style={{ width: '100%' }}
              allowClear
              onChange={(value) => updateFilterParam({ tahapanFU: value })}
              value={searchParams.get('tahapanFU') || undefined}
            >
              {leadStatuses.map((status) => (
                <Option key={status.id} value={status.id}>
                  {status.name}
                </Option>
              ))}
            </Select>
          </Col>

          {/* Filter Status Follow Up */}
          <Col xs={24} sm={12} md={8} lg={4}>
            <Select
              placeholder="Status Follow Up"
              style={{ width: '100%' }}
              allowClear
              onChange={(value) => updateFilterParam({ StatusFU: value })}
              value={searchParams.get('StatusFU') || undefined}
            >
              {leadFollowUpStatuses.map((status) => (
                <Option key={status.id} value={status.id}>
                  {status.name}
                </Option>
              ))}
            </Select>
          </Col>

          {/* Filter Provinsi (Dengan Fitur Search Dropdown) */}
          <Col xs={24} sm={12} md={8} lg={5}>
            <Select
              placeholder="Filter Provinsi"
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option: any) =>
                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
              onChange={(value) => updateFilterParam({ provinsi_id: value })}
              value={searchParams.get('provinsi_id') || undefined}
            >
              {provinces.map((prov) => (
                <Option key={prov.id_provinsi} value={prov.id_provinsi}>
                  {prov.nama_provinsi}
                </Option>
              ))}
            </Select>
          </Col>

          {/* Filter Property Area (Jika Memiliki Hak Akses) */}
          {shouldShowPropertyAreaFilter && (
            <Col xs={24} sm={12} md={8} lg={5}>
              <Select
                placeholder="Property Area"
                style={{ width: '100%' }}
                allowClear
                onChange={(value) => updateFilterParam({ property_area_id: value })}
                value={searchParams.get('property_area_id') || undefined}
              >
                {propertyAreas.map((area) => (
                  <Option key={area.id} value={area.id}>
                    {area.name}
                  </Option>
                ))}
              </Select>
            </Col>
          )}

          {/* Filter Sumber Leads */}
          <Col xs={24} sm={12} md={8} lg={shouldShowPropertyAreaFilter ? 4 : 5}>
            <Select
              placeholder="Sumber Leads"
              style={{ width: '100%' }}
              allowClear
              onChange={(value) => updateFilterParam({ source: value })}
              value={searchParams.get('source') || undefined}
            >
              {leadSources.map((source) => (
                <Option key={source.id} value={source.id}>
                  {source.name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        {/* Banner Indikator Filter Aktif */}
        {hasActiveFilters && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '8px',
              padding: '8px 12px',
              marginBottom: '16px',
              background: 'rgba(123, 170, 24, 0.08)',
              borderRadius: '8px',
              border: '1px solid rgba(123, 170, 24, 0.2)',
            }}
          >
            <span style={{ fontSize: '12px', fontWeight: 600, color: '#55790f', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <FilterOutlined /> Filter Aktif:
            </span>
            {searchParams.get('search') && (
              <Tag
                closable
                onClose={() => updateFilterParam({ search: undefined })}
                color="blue"
              >
                Pencarian: "{searchParams.get('search')}"
              </Tag>
            )}
            {searchParams.get('provinsi_id') && (
              <Tag
                closable
                onClose={() => updateFilterParam({ provinsi_id: undefined })}
                color="green"
              >
                Provinsi: {activeProvinceName}
              </Tag>
            )}
            {searchParams.get('tahapanFU') && (
              <Tag
                closable
                onClose={() => updateFilterParam({ tahapanFU: undefined })}
                color="orange"
              >
                Tahapan:{' '}
                {leadStatuses.find((s) => s.id === searchParams.get('tahapanFU'))?.name ||
                  searchParams.get('tahapanFU')}
              </Tag>
            )}
            {searchParams.get('StatusFU') && (
              <Tag
                closable
                onClose={() => updateFilterParam({ StatusFU: undefined })}
                color="purple"
              >
                Status FU:{' '}
                {leadFollowUpStatuses.find((s) => s.id === searchParams.get('StatusFU'))?.name ||
                  searchParams.get('StatusFU')}
              </Tag>
            )}
            {searchParams.get('property_area_id') && (
              <Tag
                closable
                onClose={() => updateFilterParam({ property_area_id: undefined })}
                color="cyan"
              >
                Area:{' '}
                {propertyAreas.find((a) => a.id === searchParams.get('property_area_id'))?.name ||
                  searchParams.get('property_area_id')}
              </Tag>
            )}
            {searchParams.get('source') && (
              <Tag
                closable
                onClose={() => updateFilterParam({ source: undefined })}
                color="geekblue"
              >
                Sumber:{' '}
                {leadSources.find((s) => s.id === searchParams.get('source'))?.name ||
                  searchParams.get('source')}
              </Tag>
            )}
          </div>
        )}

        <Table
          columns={columns}
          dataSource={leads}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} dari ${total} leads`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1500 }}
        />
      </Card>

      <ReassignLeadModal
        visible={reassignModalVisible}
        onCancel={() => setReassignModalVisible(false)}
        lead={selectedLead}
        onAssignSubmit={handleAssignSubmit}
        onReassignSubmit={handleReassignSubmit}
        message={message}
        hasPermission={hasPermission}
      />
    </div>
  );
};

export default LeadsList;
